// buildYearMonthData.ts
import {
  startOfDay,
  startOfWeek,
  getYear,
  getMonth,
  isWeekend,
} from "date-fns";
import { isRedDay } from "@/utils/date";
import { BookingType } from "@/services/types";

import type { YearMonthCell, YearMonthData } from "./types";

export type Booking = {
  date: Date;
  hours?: number | null;
  b_type?: number;
};

/** Aggregated hours for a day, split by booking type */
interface DayHours {
  total: number;
  absence: number;
  hardBooking: number;
}

export function buildYearMonthData<EmployeeId extends string | number>(
  visibleDays: Date[],
  selectedYearEmployee: EmployeeId | null,
  bookingsByEmployee: Map<EmployeeId, Booking[]>,
): YearMonthData {
  const data: YearMonthData = {};
  const monthWeekMap = new Map<string, Map<number, YearMonthCell[]>>();

  const selectedEmployeeBookings =
    selectedYearEmployee !== null
      ? bookingsByEmployee.get(selectedYearEmployee) || []
      : [];

  const hoursByDay = new Map<number, DayHours>();

  // Aggregate hours per day, split by booking type
  selectedEmployeeBookings.forEach((booking) => {
    const dayKey = startOfDay(booking.date).getTime();
    const hours = booking.hours || 0;
    const existing = hoursByDay.get(dayKey) || {
      total: 0,
      absence: 0,
      hardBooking: 0,
    };

    existing.total += hours;

    if (booking.b_type === BookingType.ABSENCE) {
      existing.absence += hours;
    } else if (booking.b_type === BookingType.HARD_BOOKING) {
      existing.hardBooking += hours;
    }
    // Soft bookings are counted in total but not in hardBooking

    hoursByDay.set(dayKey, existing);
  });

  // Build month/week structure
  visibleDays.forEach((day) => {
    const year = getYear(day);
    const month = getMonth(day);
    const weekStart = startOfWeek(day, { weekStartsOn: 1 }).getTime();
    const monthKey = `${year}-${month}`;

    if (!monthWeekMap.has(monthKey)) {
      monthWeekMap.set(monthKey, new Map());
    }

    const weekGroup = monthWeekMap.get(monthKey)!;
    const dayHours = hoursByDay.get(startOfDay(day).getTime()) || {
      total: 0,
      absence: 0,
      hardBooking: 0,
    };
    const dayNr = day.getDate();
    const isWeekendDay = isWeekend(day);

    const cell: YearMonthCell = {
      date: day,
      hours: dayHours.total,
      absenceHours: dayHours.absence,
      hardBookingHours: dayHours.hardBooking,
      dayNumber: dayNr,
      isWeekend: isWeekendDay,
      isRedDay: isRedDay(day),
    };

    const existingWeek = weekGroup.get(weekStart);
    if (existingWeek) {
      existingWeek.push(cell);
    } else {
      weekGroup.set(weekStart, [cell]);
    }
  });

  // Convert into YearMonthData shape - preserve week structure for proper wrapping
  monthWeekMap.forEach((weeks, key) => {
    const [yearStr, monthStr] = key.split("-").map(Number);
    if (!data[yearStr]) {
      data[yearStr] = {};
    }
    // Keep weeks as separate arrays instead of flattening
    data[yearStr][monthStr] = Array.from(weeks.values());
  });

  return data;
}
